const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

const ACCESS_KEYS = ['applicantToken', 'applicant_token'] as const;
const REFRESH_KEYS = ['applicantRefreshToken', 'applicant_refresh_token'] as const;

let refreshInFlight: Promise<string | null> | null = null;

const getFirstLocalStorageValue = (keys: readonly string[]) => {
    for (const key of keys) {
        const value = localStorage.getItem(key);
        if (value) {
            return value;
        }
    }
    return null;
};

export const getApplicantAccessToken = () => getFirstLocalStorageValue(ACCESS_KEYS);

export const getApplicantRefreshToken = () => getFirstLocalStorageValue(REFRESH_KEYS);

export const setApplicantTokens = (accessToken: string, refreshToken?: string | null) => {
    localStorage.setItem('applicantToken', accessToken);

    if (refreshToken && refreshToken.trim()) {
        localStorage.setItem('applicantRefreshToken', refreshToken);
    }
};

export const clearApplicantSession = () => {
    for (const key of ACCESS_KEYS) {
        localStorage.removeItem(key);
    }
    for (const key of REFRESH_KEYS) {
        localStorage.removeItem(key);
    }

    localStorage.removeItem('applicantInfo');
};

const extractTokenValue = (payload: unknown, keys: string[]): string | null => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    const source = payload as Record<string, unknown>;
    const nested = source.data && typeof source.data === 'object' ? (source.data as Record<string, unknown>) : null;

    for (const key of keys) {
        const direct = source[key];
        if (typeof direct === 'string' && direct.trim()) {
            return direct;
        }

        const nestedValue = nested?.[key];
        if (typeof nestedValue === 'string' && nestedValue.trim()) {
            return nestedValue;
        }
    }

    return null;
};

export const extractApplicantAccessToken = (payload: unknown) =>
    extractTokenValue(payload, ['token', 'access_token', 'accessToken']);

export const extractApplicantRefreshToken = (payload: unknown) =>
    extractTokenValue(payload, ['refresh_token', 'refreshToken']);

export const refreshApplicantAccessToken = async (): Promise<string | null> => {
    const refreshToken = getApplicantRefreshToken();
    if (!refreshToken) {
        return null;
    }

    if (refreshInFlight) {
        return refreshInFlight;
    }

    refreshInFlight = (async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/applicant/refresh`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    refresh_token: refreshToken,
                    refreshToken,
                    token: refreshToken,
                }),
            });

            if (!response.ok) {
                return null;
            }

            const data = await response.json().catch(() => ({}));
            const nextAccessToken = extractApplicantAccessToken(data);
            const nextRefreshToken = extractApplicantRefreshToken(data);

            if (!nextAccessToken) {
                return null;
            }

            setApplicantTokens(nextAccessToken, nextRefreshToken);
            return nextAccessToken;
        } catch {
            return null;
        } finally {
            refreshInFlight = null;
        }
    })();

    return refreshInFlight;
};

export const isUnauthorizedResponse = (response: Response) => response.status === 401 || response.status === 403;

export const applicantFetch = async (
    input: RequestInfo | URL,
    init: RequestInit = {},
    options: { retryOnAuthFailure?: boolean } = {}
): Promise<Response> => {
    const { retryOnAuthFailure = true } = options;

    const accessToken = getApplicantAccessToken();
    const headers = new Headers(init.headers || {});
    if (accessToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${accessToken}`);
    }

    const firstResponse = await fetch(input, {
        ...init,
        credentials: init.credentials ?? 'include',
        headers,
    });

    if (!retryOnAuthFailure || !isUnauthorizedResponse(firstResponse)) {
        return firstResponse;
    }

    const refreshedAccessToken = await refreshApplicantAccessToken();
    if (!refreshedAccessToken) {
        return firstResponse;
    }

    const retryHeaders = new Headers(init.headers || {});
    retryHeaders.set('Authorization', `Bearer ${refreshedAccessToken}`);

    return fetch(input, {
        ...init,
        credentials: init.credentials ?? 'include',
        headers: retryHeaders,
    });
};
